import React from "react";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { TCaseManagementCompanyForm } from "../../../../domain/codecs/form/ManagementCompanyForm";
import { ManagementCompany, CasesManagementCompanyLink, TCasesManagementCompanyLink } from "../../../../domain/codecs/ManagementCompany";
import { CRMCodecEditForm } from "../../components/CRMCodecEditForm/CRMCodecEditForm";
import { CRMSpacingColumn } from "../../components/CRMSpacingColumn/CRMSpacingColumn";
import { CRMSpacingRow } from "../../components/CRMSpacingRow/CRMSpacingRow";
import { CRMSpacer } from "../../components/CRMSpacer/CRMSpacer";
import { CRMGenericEditModalWithText } from "../../components/CRMGenericEditModalWithText/CRMGenericEditModalWithText";
import { CRMTitleForCard } from "../../components/CRMTitleForCard/CRMTitleForCard";
import { CRMPadding } from "../../components/Simple/CRMPadding/CRMPadding";
import { CRMParagraph } from "../../components/Simple/CRMParagraph/CRMParagraph";
import { CRMButtonTertiary } from "../../components/CRMButtonTertiary/CRMButtonTertiary";
import CRMInputLabelAndErrorWrapComponent from "../../components/CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";

type TProps = {
    form: TCaseManagementCompanyForm;
    transactionType: string;
    dispatch: TContainerStateProps["dispatch"];
};

type TLinkKey = keyof TCasesManagementCompanyLink;

export const CRMLegalCaseDetailsManagementCompanyContainer = (props: TProps): JSX.Element => {
    const isTransferOrRemortgage = props.transactionType === "transfer" || props.transactionType === "remortgage";
    const packFields: Array<TLinkKey> = props.transactionType === "sale" ? [] : ["form_type", "pack_ordered"];
    const consentFields: Array<TLinkKey> = isTransferOrRemortgage ? [] : ["consent_obtained", "consent_not_required"];
    const linkHideFields: Array<TLinkKey> = [
        "id", "case_id", "management_company_id", "linked_document_id", "created_at", "updated_at",
        ...packFields,
        ...consentFields,
    ];

    const updateLink = (link: typeof props.form.edited.link) => props.dispatch({
        type: "LEGAL_CASE_MCO_UPDATE",
        payload: {
            ...props.form,
            edited: {
                ...props.form.edited,
                link: { ...props.form.edited.link, ...link },
            },
        },
    });

    const updateCompany = (company: typeof props.form.edited.company) => props.dispatch({
        type: "LEGAL_CASE_MCO_UPDATE",
        payload: {
            ...props.form,
            edited: {
                ...props.form.edited,
                company: { ...props.form.edited.company, ...company },
            },
        },
    });

    return (
        <CRMSpacingColumn spacing="medium">
            {/* SHARED COMPANY KNOWLEDGE — edited in a modal, propagates across cases */}
            <CRMInputLabelAndErrorWrapComponent label="Company details (shared)">
                <CRMGenericEditModalWithText
                    modalSize="large"
                    text={props.form.edited.company.name}
                    body={(setIsOpen) => (
                        <CRMPadding size="large">
                            <CRMTitleForCard>
                                Management company details
                            </CRMTitleForCard>
                            <CRMSpacer size="large" />
                            <CRMParagraph>
                                These details apply to this company everywhere it's used, across every case.
                            </CRMParagraph>
                            <CRMSpacer size="medium" />
                            <CRMCodecEditForm
                                codec={ManagementCompany}
                                model={props.form.edited.company}
                                validationErrors={props.form.validationErrors}
                                errorKeyPrefix="edited.company"
                                hideFields={["id", "name_normalised", "created_at", "updated_at"]}
                                columns={2}
                                onChange={updateCompany}
                            />
                            <CRMSpacer size="large" />
                            <CRMSpacingRow spacing="medium" justifyContent="end">
                                <CRMButtonTertiary
                                    label="Close"
                                    onClick={() => setIsOpen(false)}
                                />
                            </CRMSpacingRow>
                        </CRMPadding>
                    )}
                />
            </CRMInputLabelAndErrorWrapComponent>

            {/* CASE-SPECIFIC LINK FIELDS — single column for the narrow panel */}
            <CRMCodecEditForm
                codec={CasesManagementCompanyLink}
                model={props.form.edited.link}
                validationErrors={props.form.validationErrors}
                errorKeyPrefix="edited.link"
                hideFields={linkHideFields}
                columns={1}
                showDeferDateLabels={true}
                onChange={updateLink}
            />
        </CRMSpacingColumn>
    );
};
